import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { ArrowForwardRounded, LeaderboardOutlined, MyLocationRounded } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { colors } from "../../theme/colors";
import utils from "../../universal/utils";
import { MAIN_SECTION_MIN_HEIGHT_SX } from "../layout/layoutConstants";
import { DIALOG_CANCEL_BUTTON_SX } from "../common/dialogActionButtonSx";
import LeaderboardPodium from "./LeaderboardPodium";
import LeaderboardTable from "./LeaderboardTable";
import LeaderboardTabs from "./LeaderboardTabs";
import CountrySelect from "../common/CountrySelect.jsx";
import OverlayLoader from "../common/OverlayLoader";
import { useLenis, useLenisSyncRef, scheduleLenisScrollbarSync } from "../common/lenisContext";
import {
    buildLeaderboardSearch,
    isLeaderboardViewerRow,
    parseLeaderboardSearch,
} from "./leaderboardFormat";

function getScopeFromTab(tabId) {
    if (tabId === "country") return "country";
    if (tabId === "friends") return "friends";
    return "global";
}

const HEADER_RANK_COLORS = Object.freeze({
    1: "#FFF0C7",
    2: "#F0F2F6",
    3: "#EBCBBB",
});

const LEADERBOARD_LIST_CAP = 1000;
const LEADERBOARD_V2_REQUEST_OPTIONS = Object.freeze({
    includeAggregates: false,
    includeExtras: false,
});

function findVisibleLeaderboardMeRow() {
    if (typeof document === "undefined") return null;

    const nodes = document.querySelectorAll("[data-leaderboard-viewer-me]");
    for (const el of nodes) {
        if (el.getClientRects().length > 0) {
            return el;
        }
    }

    return null;
}

export default function LeaderboardPage({ catalog, actions }) {
    const user = catalog?.user;
    const lb = catalog?.leaderboard || {};
    const lenis = useLenis();
    const lenisSyncRef = useLenisSyncRef();
    const history = useHistory();
    const location = useLocation();

    const [scopeTab, setScopeTab] = useState(
        () => parseLeaderboardSearch(location.search).scope,
    );
    const [countryQuery, setCountryQuery] = useState(
        () => parseLeaderboardSearch(location.search).country,
    );
    const [countryInput, setCountryInput] = useState(
        () => parseLeaderboardSearch(location.search).country || user?.country || "",
    );
    const [countries, setCountries] = useState([]);
    const [countriesLoading, setCountriesLoading] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [myRankHint, setMyRankHint] = useState(null);
    const [jumpingToMe, setJumpingToMe] = useState(false);
    const [jumpScrollEpoch, setJumpScrollEpoch] = useState(0);

    const normalizeCountryKey = useCallback((value) => {
        if (!value) return "";
        return String(value).replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    }, []);

    const setLeaderboardLocation = useCallback(
        (nextScope, country = "", { replace = false } = {}) => {
            const search = buildLeaderboardSearch(nextScope, country);
            if (search === location.search) return;

            const nextLocation = { ...location, search };
            if (replace) {
                history.replace(nextLocation);
            } else {
                history.push(nextLocation);
            }
        },
        [history, location],
    );

    useEffect(() => {
        const nextUrlState = parseLeaderboardSearch(location.search);
        const canonicalSearch = buildLeaderboardSearch(
            nextUrlState.scope,
            nextUrlState.country,
        );

        if (canonicalSearch !== location.search) {
            history.replace({ ...location, search: canonicalSearch });
        }

        setScopeTab(nextUrlState.scope);
        if (nextUrlState.scope === "country") {
            const nextCountry = nextUrlState.country;
            const nextCountryKey = normalizeCountryKey(nextCountry);
            setCountryQuery(nextCountry);
            setCountryInput(nextCountry);
            setSelectedCountry((current) => {
                if (!current) return null;
                const currentMatches =
                    normalizeCountryKey(current.displayName) === nextCountryKey ||
                    normalizeCountryKey(current.code2) === nextCountryKey ||
                    normalizeCountryKey(current.code3) === nextCountryKey;
                return currentMatches ? current : null;
            });
        } else {
            setCountryQuery("");
        }
    }, [history, location.search, normalizeCountryKey]);

    useEffect(() => {
        if (!countryInput && !countryQuery && user?.country) {
            setCountryInput(user.country);
        }
    }, [countryInput, countryQuery, user?.country]);

    useEffect(() => {
        if (!user?.userId) return;
        if (countriesLoading) return;
        if (countries.length > 0) return;

        const basePath = utils.getEdgeBasePath();
        setCountriesLoading(true);
        fetch(`${basePath}/api/v1/lobby/countries`)
            .then((r) => r.json())
            .then((data) => {
                if (data?.ok && Array.isArray(data.countries)) {
                    setCountries(data.countries);
                } else {
                    setCountries([]);
                }
            })
            .catch(() => setCountries([]))
            .finally(() => setCountriesLoading(false));
    }, [countries.length, countriesLoading, user?.userId]);

    useEffect(() => {
        if (scopeTab !== "country" || !countryQuery) return;
        if (!Array.isArray(countries) || countries.length === 0) return;

        const key = normalizeCountryKey(countryQuery);
        if (!key) return;

        const match = countries.find((country) => {
            return (
                normalizeCountryKey(country.displayName) === key ||
                normalizeCountryKey(country.code2) === key ||
                normalizeCountryKey(country.code3) === key
            );
        });
        if (!match) return;

        setSelectedCountry(match);
        setCountryInput(match.displayName);
    }, [countries, countryQuery, normalizeCountryKey, scopeTab]);

    useEffect(() => {
        if (!user?.country || countryQuery) return;
        if (!Array.isArray(countries) || countries.length === 0) return;
        if (selectedCountry) return;

        const key = normalizeCountryKey(user.country);
        if (!key) return;

        const match = countries.find((country) => {
            return (
                normalizeCountryKey(country.displayName) === key ||
                normalizeCountryKey(country.code2) === key ||
                normalizeCountryKey(country.code3) === key
            );
        });
        if (match) {
            setSelectedCountry(match);
            setCountryInput(match.displayName);
        }
    }, [
        countries,
        countryQuery,
        normalizeCountryKey,
        selectedCountry,
        user?.country,
    ]);

    useEffect(() => {
        if (scopeTab !== "country") return;
        if (countryQuery || !selectedCountry?.displayName) return;

        setCountryQuery(selectedCountry.displayName);
        setLeaderboardLocation("country", selectedCountry.displayName, {
            replace: true,
        });
    }, [
        countryQuery,
        scopeTab,
        selectedCountry?.displayName,
        setLeaderboardLocation,
    ]);

    useEffect(() => {
        setMyRankHint(null);
    }, [scopeTab]);

    useEffect(() => {
        if (!user?.userId) {
            setMyRankHint(null);
            return;
        }

        const scope = getScopeFromTab(scopeTab);
        const country =
            scope === "country"
                ? selectedCountry?.displayName ||
                  countryInput ||
                  user?.country ||
                  ""
                : undefined;

        if (scope === "country" && !country) {
            setMyRankHint(null);
            return;
        }

        const limit = scope === "friends" ? 200 : 50;
        const delayMs = scope === "country" ? 320 : 0;

        let ac = null;
        const t = window.setTimeout(() => {
            const qs = new URLSearchParams();
            qs.set("scope", scope);
            qs.set("limit", String(limit));
            if (country) qs.set("country", country);

            const basePath = utils.getEdgeBasePath();
            ac = new AbortController();

            fetch(`${basePath}/api/v1/leaderboard/me?${qs.toString()}`, {
                credentials: "include",
                signal: ac.signal,
            })
                .then((r) => r.json().catch(() => ({})))
                .then((data) => {
                    if (!data || data.ok === false) return;
                    if (typeof data.rank === "number") {
                        setMyRankHint(data.rank);
                    } else {
                        setMyRankHint(null);
                    }
                })
                .catch(() => {
                    if (!ac?.signal.aborted) setMyRankHint(null);
                });
        }, delayMs);

        return () => {
            window.clearTimeout(t);
            ac?.abort();
        };
    }, [
        user?.userId,
        scopeTab,
        countryInput,
        countryQuery,
        selectedCountry?.displayName,
        user?.country,
    ]);

    const countryForRequest =
        selectedCountry?.displayName ||
        countryQuery ||
        countryInput ||
        user?.country ||
        "";
    const countryRequestKey =
        scopeTab === "country" ? normalizeCountryKey(countryForRequest) : "";

    useEffect(() => {
        if (!user?.userId) return;
        if (typeof actions?.getLeaderboard !== "function") return;

        const scope = getScopeFromTab(scopeTab);
        if (scope === "country" && !countryForRequest) return;

        actions.getLeaderboard({
            ...LEADERBOARD_V2_REQUEST_OPTIONS,
            scope,
            country: scope === "country" ? countryForRequest : undefined,
            limit: scope === "friends" ? 200 : 50,
            offset: 0,
        });
    }, [
        actions?.getLeaderboard,
        countryRequestKey,
        scopeTab,
        user?.userId,
    ]);

    const handleScopeTabClick = useCallback(
        (nextScope) => {
            setScopeTab(nextScope);

            if (nextScope === "country") {
                const nextCountry =
                    selectedCountry?.displayName ||
                    countryQuery ||
                    user?.country ||
                    countryInput ||
                    "";
                setCountryQuery(nextCountry);
                if (nextCountry) setCountryInput(nextCountry);
                setLeaderboardLocation("country", nextCountry);
                return;
            }

            setLeaderboardLocation(nextScope);
        },
        [
            countryInput,
            countryQuery,
            selectedCountry?.displayName,
            setLeaderboardLocation,
            user?.country,
        ],
    );

    const handleCountryAutocompleteChange = useCallback(
        (event, nextValue) => {
            const nextCountry = nextValue || null;
            const nextName = nextCountry?.displayName || "";
            setSelectedCountry(nextCountry);
            setCountryInput(nextName);
            setCountryQuery(nextName);
            setLeaderboardLocation("country", nextName);
        },
        [setLeaderboardLocation],
    );

    const handleLoadMore = useCallback(() => {
        if (!user?.userId || typeof actions?.getLeaderboard !== "function") return;
        const scope =
            lb?.scope ||
            getScopeFromTab(scopeTab);
        const pageFloor = scope === "friends" ? 200 : 50;
        const pageSize = Math.max(lb?.limit || pageFloor, pageFloor);
        const nextOffset = (lb?.offset || 0) + pageSize;
        actions.getLeaderboard({
            ...LEADERBOARD_V2_REQUEST_OPTIONS,
            scope,
            country:
                scope === "country"
                    ? selectedCountry?.displayName ||
                      lb?.country?.displayName ||
                      countryInput ||
                      user?.country
                    : undefined,
            limit: pageSize,
            offset: nextOffset,
            append: true,
        });
    }, [
        user?.userId,
        user?.country,
        countryInput,
        selectedCountry?.displayName,
        lb?.offset,
        lb?.limit,
        lb?.scope,
        lb?.country,
        scopeTab,
        actions,
    ]);

    const isLoading = lb?.loading === true;
    const isLoadingRef = useRef(isLoading);
    isLoadingRef.current = isLoading;
    const rows = Array.isArray(lb?.rows) ? lb.rows : [];
    const hasRows = rows.length > 0;
    const scope = lb?.scope || getScopeFromTab(scopeTab);
    const myRow = rows.find((row) => isLeaderboardViewerRow(row, user)) || null;
    const myRank = myRow?.rank ?? myRankHint;

    const userCountryMatchesSelectedCountry =
        !user?.country ||
        !selectedCountry ||
        normalizeCountryKey(user.country) ===
            normalizeCountryKey(selectedCountry.displayName) ||
        normalizeCountryKey(user.country) ===
            normalizeCountryKey(selectedCountry.code2) ||
        normalizeCountryKey(user.country) ===
            normalizeCountryKey(selectedCountry.code3);

    const headerMyRank =
        scopeTab === "country" && !userCountryMatchesSelectedCountry
            ? null
            : myRank;

    const headerRankNumber = Number(headerMyRank);
    const headerRankColor =
        HEADER_RANK_COLORS[headerRankNumber] || "#FFFFFF";


    const showJumpToMe =
        Boolean(user?.userId) &&
        typeof headerMyRank === "number" &&
        headerMyRank >= 1 &&
        headerMyRank <= LEADERBOARD_LIST_CAP;
    const isOutsideLeaderboardCap =
        typeof headerMyRank === "number" &&
        headerMyRank > LEADERBOARD_LIST_CAP;
    const podiumPlayersData = rows.slice(0, 3);
    const playersData = rows;

    const showLoadMore =
        !isLoading &&
        lb?.total != null &&
        lb?.limit != null &&
        lb?.offset != null &&
        lb.offset + lb.limit < lb.total;

    const handleJumpToMe = useCallback(async () => {
        if (!user?.userId) return;
        const activeScope = lb?.scope || getScopeFromTab(scopeTab);
        const country =
            activeScope === "country"
                ? selectedCountry?.displayName ||
                  countryInput ||
                  user?.country
                : undefined;

        setJumpingToMe(true);
        try {
            const pageFloor = activeScope === "friends" ? 200 : 50;
            const pageSize = Math.max(lb?.limit || pageFloor, pageFloor);

            const qs = new URLSearchParams();
            qs.set("scope", activeScope);
            qs.set("limit", String(pageSize));
            if (country) qs.set("country", country);

            const basePath = utils.getEdgeBasePath();
            const resp = await fetch(
                `${basePath}/api/v1/leaderboard/me?${qs.toString()}`,
                { credentials: "include" }
            );
            const data = await resp.json().catch(() => ({}));
            if (!resp.ok || data?.ok === false) {
                throw new Error(
                    data?.message ||
                        data?.error ||
                        `Failed to find your rank (HTTP ${resp.status})`
                );
            }

            if (typeof data?.rank === "number") {
                setMyRankHint(data.rank);
            } else {
                setMyRankHint(null);
            }

            if (typeof data?.rank !== "number" || data.rank < 1) {
                throw new Error("Could not determine your rank on this leaderboard");
            }
            const targetRank = Math.min(data.rank, LEADERBOARD_LIST_CAP);
            if (typeof actions?.getLeaderboard !== "function") {
                setJumpingToMe(false);
                return;
            }

            const targetOffset = Math.floor((targetRank - 1) / pageSize) * pageSize;
            const offsetsToLoad = targetOffset > 0 ? [0, targetOffset] : [0];

            for (const loadOffset of offsetsToLoad) {
                const result = await actions.getLeaderboard({
                    ...LEADERBOARD_V2_REQUEST_OPTIONS,
                    scope: activeScope,
                    country,
                    limit: pageSize,
                    offset: loadOffset,
                    append: loadOffset > 0,
                    preserveAggregatesOnLoad: loadOffset > 0,
                });
                if (!result?.ok) {
                    throw new Error(
                        result?.error?.message || "Failed to load leaderboard"
                    );
                }
            }
            setJumpScrollEpoch((n) => n + 1);
        } catch (e) {
            setJumpingToMe(false);
            if (typeof actions?.leaderboardLoadError === "function") {
                actions.leaderboardLoadError(
                    e?.message || "Failed to find your rank"
                );
            }
        }
    }, [
        user?.userId,
        user?.country,
        countryInput,
        selectedCountry?.displayName,
        lb?.scope,
        lb?.limit,
        scopeTab,
        actions,
    ]);

    useEffect(() => {
        if (!jumpScrollEpoch) return;

        let cancelled = false;
        let retryId = null;
        let fallbackClearId = null;
        let attempts = 0;
        const maxAttempts = 25;

        const finishJump = () => {
            if (cancelled) return;
            if (fallbackClearId != null) {
                window.clearTimeout(fallbackClearId);
                fallbackClearId = null;
            }
            setJumpingToMe(false);
            setJumpScrollEpoch(0);
        };

        const startScroll = (el) => {
            if (cancelled) return;

            scheduleLenisScrollbarSync(lenisSyncRef);
            if (typeof lenis?.resize === "function") {
                lenis.resize();
            }

            setJumpingToMe(false);

            if (lenis) {
                const rect = el.getBoundingClientRect();
                const target =
                    lenis.scroll +
                    rect.top -
                    window.innerHeight / 2 +
                    rect.height / 2;
                lenis.scrollTo(Math.max(0, target), {
                    duration: 1.15,
                    onComplete: finishJump,
                });
            } else {
                el.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                    inline: "nearest",
                });
            }

            fallbackClearId = window.setTimeout(finishJump, 1800);
        };

        const waitAndScroll = () => {
            if (cancelled) return;
            if (isLoadingRef.current) {
                retryId = window.setTimeout(waitAndScroll, 80);
                return;
            }

            const el = findVisibleLeaderboardMeRow();
            if (!el) {
                attempts += 1;
                if (attempts >= maxAttempts) {
                    finishJump();
                    return;
                }
                retryId = window.setTimeout(waitAndScroll, 80);
                return;
            }

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (cancelled) return;
                    const visible = findVisibleLeaderboardMeRow();
                    if (!visible) {
                        retryId = window.setTimeout(waitAndScroll, 80);
                        return;
                    }
                    startScroll(visible);
                });
            });
        };

        retryId = window.setTimeout(waitAndScroll, 80);

        return () => {
            cancelled = true;
            if (retryId != null) window.clearTimeout(retryId);
            if (fallbackClearId != null) window.clearTimeout(fallbackClearId);
        };
    }, [jumpScrollEpoch, lenis, lenisSyncRef]);

    return (
        <>
            <OverlayLoader
                open={jumpingToMe}
            />
            <Box
                sx={{
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                    boxSizing: "border-box",
                    flex: 1,
                    minHeight: MAIN_SECTION_MIN_HEIGHT_SX,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-start",
                }}
            >
            <Box
                sx={{
                    width: "100%",
                    minWidth: 0,
                    maxWidth: "100%",
                    minHeight: MAIN_SECTION_MIN_HEIGHT_SX,
                    boxSizing: "border-box",
                    display: "flex",
                    flexDirection: "column",
                    background: alpha(colors.background.base, 0.5),
                    border: `1px solid ${colors.border.default}`,
                    borderRadius: "6px",
                    boxShadow: `0 4px 24px rgba(0, 0, 0, 0.4)`,
                    p: { xs: "1rem", md: "1.5rem" },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        alignItems: { xs: "stretch", md: "center" },
                        justifyContent: "space-between",
                        gap: { xs: 0, md: "1.25rem" },
                        mt: { xs: "1rem", md: 0 },
                        width: "100%",
                    }}
                >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <LeaderboardTabs
                            scopeTab={scopeTab}
                            onScopeTabClick={handleScopeTabClick}
                        />
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            alignItems: { xs: "stretch", sm: "center" },
                            gap: "0.75rem",
                            width: { xs: "100%", md: "auto" },
                            mt: { xs: "3rem", md: 0 },
                            flexShrink: 0,
                        }}
                    >
                        {user && scopeTab === "country" ? (
                            <CountrySelect
                                countries={countries}
                                loading={countriesLoading}
                                value={selectedCountry}
                                onChange={handleCountryAutocompleteChange}
                                onInputChange={setCountryInput}
                                sx={{ width: { xs: "100%", sm: "18rem" } }}
                            />
                        ) : null}

                        <Box
                            aria-label="Leaderboard player actions"
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: "0.8rem",
                            width: { xs: "100%", md: "auto" },
                            minWidth: {
                                md: showJumpToMe ? "21rem" : "13.5rem",
                            },
                            p: "0.5rem",
                            boxSizing: "border-box",
                            borderRadius: "12px",
                            border: "1px solid rgba(255,255,255,0.07)",
                            background: "linear-gradient(165deg, rgba(18,18,22,0.9), rgba(10,10,12,0.97))",
                            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04), 0 10px 28px rgba(0,0,0,0.22)",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "0.7rem",
                                minWidth: 0,
                                flex: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    width: "2.15rem",
                                    height: "2.15rem",
                                    flexShrink: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: "9px",
                                    border: "1px solid rgba(255,255,255,0.075)",
                                    background: "rgba(255,255,255,0.03)",
                                    color: "rgba(255,255,255,0.62)",
                                }}
                            >
                                <LeaderboardOutlined sx={{ fontSize: "1.1rem" }} />
                            </Box>

                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography
                                    sx={{
                                        color: colors.text.muted,
                                        fontSize: "0.58rem",
                                        fontWeight: 650,
                                        letterSpacing: "0.1em",
                                        lineHeight: 1,
                                        textTransform: "uppercase",
                                    }}
                                >
                                    Your rank
                                </Typography>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "baseline",
                                        gap: "0.55rem",
                                        mt: "0.3rem",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: headerRankColor,
                                            fontFamily: "JetBrains Mono, monospace",
                                            fontSize: "0.95rem",
                                            fontWeight: 800,
                                            lineHeight: 1,
                                            textShadow:
                                                headerRankNumber >= 1 && headerRankNumber <= 3
                                                    ? `0 0 14px ${alpha(headerRankColor, 0.28)}`
                                                    : "none",
                                        }}
                                    >
                                        {headerMyRank != null ? "#" + headerMyRank : "—"}
                                    </Typography>
                                    {isOutsideLeaderboardCap ? (
                                        <Typography
                                            sx={{
                                                color: "rgba(255,255,255,0.34)",
                                                fontSize: "0.54rem",
                                                fontWeight: 650,
                                                letterSpacing: "0.07em",
                                                lineHeight: 1,
                                                textTransform: "uppercase",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            Outside top 1K
                                        </Typography>
                                    ) : null}
                                </Box>
                            </Box>
                        </Box>

                        {showJumpToMe ? (
                            <Box
                                component="button"
                                type="button"
                                disabled={!user?.userId || jumpingToMe || isLoading}
                                onClick={handleJumpToMe}
                                sx={{
                                    ...DIALOG_CANCEL_BUTTON_SX,
                                    minWidth: "9.5rem",
                                    height: "2.5rem",
                                    px: { xs: "1rem", md: "1.15rem" },
                                    fontSize: "0.78rem",
                                    fontWeight: 650,
                                    letterSpacing: "0.025em",
                                    textTransform: "none",
                                    borderRadius: "10px",
                                    flexShrink: 0,
                                    "&:disabled": {
                                        opacity: 0.5,
                                        cursor: "not-allowed",
                                        pointerEvents: "none",
                                    },
                                }}
                            >
                                <MyLocationRounded
                                    sx={{
                                        mr: "0.5rem",
                                        fontSize: "1rem",
                                        opacity: 0.9,
                                        flexShrink: 0,
                                    }}
                                />
                                Jump to me
                            </Box>
                        ) : null}
                        </Box>
                    </Box>
                </Box>

                <Box
                    sx={{
                        mt: 2,
                        width: "100%",
                        minWidth: 0,
                    }}
                >
                    {!user && (
                        <Typography sx={{ color: colors.text.secondary }}>
                            Please wait...
                        </Typography>
                    )}

                    {user && lb?.error && (
                        <Box
                            sx={{
                                mt: 1,
                                width: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Typography
                                sx={{
                                    color: colors.text.secondary,
                                    fontWeight: 500,
                                    textAlign: "center",
                                }}
                            >
                                {lb.error}
                            </Typography>
                        </Box>
                    )}

                    {user && !isLoading && !hasRows && !lb?.error && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight: { xs: "min(52vh, 420px)", md: "min(56vh, 560px)" },
                                px: { xs: 0, sm: 2 },
                                py: { xs: 4, sm: 6 },
                            }}
                        >
                            <Box
                                sx={{
                                    position: "relative",
                                    width: "100%",
                                    maxWidth: "32rem",
                                    overflow: "hidden",
                                    textAlign: "center",
                                    px: { xs: "1.5rem", sm: "3rem" },
                                    py: { xs: "2.5rem", sm: "3.25rem" },
                                    boxSizing: "border-box",
                                    borderRadius: "18px",
                                    border: "1px solid rgba(246,185,76,0.16)",
                                    background: "radial-gradient(circle at 50% 0%, rgba(246,185,76,0.1), transparent 46%), linear-gradient(155deg, rgba(18,18,22,0.94), rgba(8,8,10,0.88))",
                                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.045), 0 24px 64px rgba(0,0,0,0.38), 0 0 72px rgba(246,185,76,0.045)",
                                    "&::before": {
                                        content: '""',
                                        position: "absolute",
                                        top: 0,
                                        left: "50%",
                                        width: "8rem",
                                        height: "1px",
                                        transform: "translateX(-50%)",
                                        background: "linear-gradient(90deg, transparent, #F6B94C, transparent)",
                                        boxShadow: "0 0 18px rgba(246,185,76,0.45)",
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: "3.25rem",
                                        height: "3.25rem",
                                        mx: "auto",
                                        mb: "1.25rem",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        borderRadius: "14px",
                                        color: colors.primary.main,
                                        border: "1px solid rgba(246,185,76,0.22)",
                                        background: "rgba(246,185,76,0.08)",
                                        boxShadow: "0 10px 30px rgba(246,185,76,0.08)",
                                    }}
                                >
                                    <LeaderboardOutlined sx={{ fontSize: "1.45rem" }} />
                                </Box>

                                <Typography
                                    sx={{
                                        fontFamily: "JetBrains Mono, monospace",
                                        fontSize: "0.66rem",
                                        fontWeight: 750,
                                        letterSpacing: "0.12em",
                                        textTransform: "uppercase",
                                        color: colors.primary.main,
                                        mb: "0.75rem",
                                    }}
                                >
                                    No ranked players
                                </Typography>
                                <Typography
                                    sx={{
                                        color: colors.text.primary,
                                        fontSize: { xs: "1.08rem", sm: "1.3rem" },
                                        fontWeight: 650,
                                        lineHeight: 1.35,
                                        letterSpacing: "-0.01em",
                                    }}
                                >
                                    {scopeTab === "country"
                                        ? "Nothing on the board in " +
                                          (selectedCountry?.displayName || "this country") +
                                          " yet"
                                        : scopeTab === "friends"
                                            ? "Your friends board is quiet"
                                            : "The leaderboard is taking shape"}
                                </Typography>
                                <Typography
                                    sx={{
                                        maxWidth: "25rem",
                                        mx: "auto",
                                        mt: "0.75rem",
                                        color: colors.text.secondary,
                                        fontSize: { xs: "0.8rem", sm: "0.88rem" },
                                        fontWeight: 400,
                                        lineHeight: 1.65,
                                    }}
                                >
                                    {scopeTab === "country"
                                        ? "Choose another country or return to Global while players establish their rank."
                                        : scopeTab === "friends"
                                            ? "Ranked friends will appear here after they join the leaderboard."
                                            : "Verified players will appear here as soon as their first ranking is established."}
                                </Typography>

                                {scopeTab !== "global" ? (
                                    <Box
                                        component="button"
                                        type="button"
                                        onClick={() => handleScopeTabClick("global")}
                                        sx={{
                                            ...DIALOG_CANCEL_BUTTON_SX,
                                            gap: "0.5rem",
                                            mt: "1.85rem",
                                        }}
                                    >
                                        Explore global rankings
                                        <ArrowForwardRounded sx={{ fontSize: "1rem" }} />
                                    </Box>
                                ) : null}
                            </Box>
                        </Box>
                    )}

                    {user && !lb?.error && hasRows && (
                        <Box sx={{ display: { xs: "none", md: "block" }, mt: { md: "2.5rem" } }}>
                            <LeaderboardPodium
                                podiumPlayersData={podiumPlayersData}
                                user={user}
                                avatarCacheBust={catalog?.avatarCacheBust}
                            />
                        </Box>
                    )}

                    {user && !lb?.error && (hasRows || isLoading) && (
                        <LeaderboardTable
                            playersData={hasRows ? playersData : []}
                            user={user}
                            scope={scope}
                            isLoading={isLoading}
                            hasAnyRows={hasRows}
                            showLoadMore={showLoadMore}
                            onLoadMore={handleLoadMore}
                            beyondPodiumPlaceholderText={
                                hasRows &&
                                    playersData.length === 0 &&
                                    !isLoading
                                    ? scopeTab === "friends"
                                        ? "No more friends..."
                                        : "No more players..."
                                    : null
                            }
                        />
                    )}
                </Box>
            </Box>
        </Box>
        </>
    );
}
